/* eslint react-hooks/exhaustive-deps: "warn" */

import { useState, type FormEvent } from 'react';

import cn from 'classnames';

import { Badge } from 'ui/atoms/Badge/Badge';
import { Button } from 'ui/atoms/Button/Button';
import { Icon } from 'ui/atoms/Icon/Icon';
import { Input } from 'ui/atoms/Input/Input';

import { EMAIL_RE, submitPricingLead } from 'features/product-pricing/helpers/submitPricingLead';

import styles from './PricingQuoteForm.module.scss';

interface Props {
  chosen: string[];
}

interface FormState {
  email: string;
  fullName: string;
  phone: string;
  notes: string;
}

const INITIAL_FORM: FormState = { email: '', fullName: '', phone: '', notes: '' };

const REQUIRED_FIELDS: (keyof FormState)[] = ['email', 'fullName', 'phone'];

export const PricingQuoteForm = ({ chosen }: Props) => {
  const [form, setForm] = useState(INITIAL_FORM);
  const [errors, setErrors] = useState<Partial<Record<keyof FormState, string>>>({});
  const [submitAttempted, setSubmitAttempted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [apiError, setApiError] = useState<string>();
  const [submitted, setSubmitted] = useState(false);

  const updateField = (field: keyof FormState) => (value: string) => {
    setForm((prev) => ({ ...prev, [field]: value }));
    setErrors((prev) => (prev[field] ? { ...prev, [field]: undefined } : prev));
  };

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    setSubmitAttempted(true);
    setApiError(undefined);

    const nextErrors: typeof errors = {};

    REQUIRED_FIELDS.forEach((field) => {
      if (!form[field].trim()) {
        nextErrors[field] = 'This field is required.';
      }
    });

    if (form.email && !EMAIL_RE.test(form.email)) {
      nextErrors.email = 'Please use a valid email address.';
    }

    setErrors(nextErrors);

    if (Object.keys(nextErrors).length > 0 || chosen.length === 0) {
      return;
    }

    const message = [`DATA BLOCKS: ${chosen.join(', ')}`, form.notes.trim() && `NOTES: ${form.notes.trim()}`]
      .filter(Boolean)
      .join('\n');

    setSubmitting(true);
    const error = await submitPricingLead({ fullName: form.fullName, email: form.email, phone: form.phone, message });
    setSubmitting(false);

    if (error) {
      setApiError(error);
      return;
    }

    setSubmitted(true);
  };

  if (submitted) {
    return (
      <section id="quote" className={styles.card} role="status">
        <div className={styles.heading}>
          <span className={cn(styles.step, styles.stepDone)}>
            <Icon type="check-10" />
          </span>
          <h2 className={styles.title}>Request received</h2>
        </div>

        <p className={styles.text}>
          We are checking coverage for your selection now. Want to talk sooner?{' '}
          <a className={styles.link} href="https://www.globaldatabase.com/book-a-demo">
            Pick a time for a call
          </a>
        </p>
      </section>
    );
  }

  return (
    <section id="quote" className={styles.card}>
      <form className={styles.form} onSubmit={handleSubmit} noValidate>
        <div className={styles.head}>
          <div className={styles.heading}>
            <span className={styles.step}>2</span>
            <h2 className={styles.title}>Get your quote</h2>
          </div>

          <div className={styles.chosen}>
            {chosen.length > 0 ? (
              chosen.map((label) => (
                <Badge size="sm" key={label} className={styles.chip}>
                  {label}
                </Badge>
              ))
            ) : (
              <span className={styles.text}>No data selected</span>
            )}
          </div>
        </div>

        <div className={styles.fields}>
          <label className={styles.field}>
            <span className={styles.label}>Work email</span>
            <Input
              htmlType="email"
              name="email"
              value={form.email}
              onChange={updateField('email')}
              error={errors.email}
              placeholder="you@company.com"
              autoComplete="email"
              size="md"
              fullWidth
            />
          </label>

          <label className={styles.field}>
            <span className={styles.label}>Full name</span>
            <Input
              name="fullname"
              value={form.fullName}
              onChange={updateField('fullName')}
              error={errors.fullName}
              placeholder="Your name"
              autoComplete="name"
              size="md"
              fullWidth
            />
          </label>

          <label className={styles.field}>
            <span className={styles.label}>Phone number</span>
            <Input
              htmlType="tel"
              name="phone"
              value={form.phone}
              onChange={updateField('phone')}
              error={errors.phone}
              placeholder="+44 20 0000 0000"
              autoComplete="tel"
              size="md"
              fullWidth
            />
          </label>
        </div>

        <div className={styles.bottom}>
          <label className={styles.notes}>
            <span className={styles.label}>What do you need?</span>
            <Input
              htmlType="textarea"
              name="message"
              rows={2}
              value={form.notes}
              onChange={updateField('notes')}
              placeholder="e.g. UK and Irish companies, about 5,000 a year, via API"
              className={styles.textarea}
            />
          </label>

          <Button
            type="blue"
            size="xl"
            htmlType="submit"
            suffixIcon="arrow-7"
            loading={submitting}
            className={styles.submit}
          >
            Get my quote
          </Button>
        </div>

        {submitAttempted && chosen.length === 0 && (
          <p className={styles.warning} role="alert">
            Tick at least one data type above to get your quote.
          </p>
        )}

        {apiError && (
          <p className={styles.warning} role="alert">
            {apiError}
          </p>
        )}
      </form>
    </section>
  );
};
